import { useEffect, useRef } from 'react'

import { getFromStorage, setStorageValue } from '../helpers/helpers'

const CHECK_INTERVAL = 60 * 1000 // Check every minute

const isReloadTime = (reloadTimeData: ReloadTimeData) => {
  const now = new Date()
  const hours = now.getHours()
  const minutes = now.getMinutes()

  return hours === reloadTimeData.reloadHour && minutes === reloadTimeData.reloadMinute
}

const defaultReloadTimeData = {
  reloadHour: 3, // 3am
  reloadMinute: 0, // 3:00am
}

// NOTE: time is local to the machine
export type ReloadTimeData = {
  reloadHour: number
  reloadMinute: number
}

export interface UseAutoReloadProps {
  storageKey: string
  /**
   * Return true to skip reloading
   */
  skipReloadCheck?: () => boolean
  reloadTimeData?: ReloadTimeData
}

/**
 * @deprecated Blind "reload at a fixed local time" — doesn't know whether a new build is
 * actually out, and misses its window entirely if the tab is asleep through the exact
 * check minute (a real cause of stale kiosk tabs). Superseded by `useKioskSafeReload`,
 * which reloads only when a new build is actually detected. Once that's verified in
 * production for the 3 call sites still using this hook (PackageRoom, Market Shop, Market
 * Shop Restaurant), switch them over and delete this hook. See EL-13298.
 */
export const useAutoPageReload = ({
  storageKey,
  skipReloadCheck,
  reloadTimeData = defaultReloadTimeData,
}: UseAutoReloadProps) => {
  const skipReloadCheckRef = useRef(skipReloadCheck)
  skipReloadCheckRef.current = skipReloadCheck

  useEffect(() => {
    const checkAndReload = () => {
      if (!isReloadTime(reloadTimeData)) return

      const today = new Date().toDateString()

      // Note: Prevent multiple reloads on the same day
      if (getFromStorage(storageKey, null) === today) return

      if (skipReloadCheckRef.current?.()) return

      setStorageValue(storageKey, today)
      window.location.reload()
    }

    const checkInterval = setInterval(checkAndReload, CHECK_INTERVAL)

    return () => clearInterval(checkInterval)
  }, [storageKey, reloadTimeData])
}
